// formatNumber.js

/**
 * Formats a number to a string with dynamic decimal places and as US dollars.
 *
 * @param {number} value The number to format.
 * @param formatAsUSD
 * @param asCurrency
 * @param includeCommas
 * @param currency
 * @returns {string} The formatted number as a US dollar string.
 */
export function formatNumber(
    value: number,
    formatAsUSD = false,
    includeCommas = false,
    currency = 'USD',
    market = false,
) {
    const number = Number(value);
    if (isNaN(number)) {
        console.error('formatNumber: Provided value cannot be converted to a number:', value);
        return 'not available'; // Return an empty string or some error indicator as appropriate
    }

    let formattedNumber;

    const significantDigits = 2; // Default significant digits
    formattedNumber = number.toFixed(significantDigits).replace(/\.?0+$/, '');
    if (includeCommas && currency === 'USD') {
        if (number >= 1) {
            const options = {
                minimumFractionDigits: number < 1000 ? 2 : 0,
                maximumFractionDigits: number < 1000 ? 2 : 0,
            };
            formattedNumber = new Intl.NumberFormat('en-US', options).format(number);
        } else if (number >= 0.01) {
            formattedNumber = number.toFixed(4).replace(/0+$/, '');
        } else if (number <= 0.01) {
            if (/e-/.test(number.toString())) {
                // Determine the number of digits to show based on the exponent
                const scientificNotationMatch = number.toExponential().match(/e-(\d+)/);
                if (scientificNotationMatch) {
                    const exponent = parseInt(scientificNotationMatch[1], 10);
                    const significantDigits = Math.max(20, exponent); // Adjust the number of significant digits based on exponent
                    const trimmedNumber = number.toFixed(significantDigits).replace(/\.0+$/, '');
                    const nonZeroMatch = trimmedNumber.match(/0\.0*([1-9]\d*)/);
                    if (nonZeroMatch) {
                        formattedNumber = `0.0...${nonZeroMatch[1].substring(0, 3)}`; // Format it as 0.0... followed by the first few non-zero digits
                    } else {
                        formattedNumber = trimmedNumber; // Fallback if the regex doesn't match (should not normally happen)
                    }
                }
            } else {
                formattedNumber = number.toFixed(8).replace(/0+$/, '');
            }
        }
    }
    if (includeCommas && currency === 'TMN') {
        const scientificNotationMatch = number.toExponential().match(/e-(\d+)/);
        if (scientificNotationMatch) {
            const exponent = parseInt(scientificNotationMatch[1], 10);
            const significantDigits = Math.max(15, exponent); // Adjust the number of significant digits based on exponent
            const trimmedNumber = number.toFixed(significantDigits).replace(/\.0+$/, '');
            const nonZeroMatch = trimmedNumber.match(/0\.0*([1-9]\d*)/);
            if (nonZeroMatch) {
                formattedNumber = `${trimmedNumber.substring(0, 15)}`; // Format it as 0.0... followed by the first few non-zero digits
            } else {
                formattedNumber = trimmedNumber; // Fallback if the regex doesn't match (should not normally happen)
            }
        } else {
            const parts = number
                .toFixed(number > 100 ? 0 : 2)
                .toString()
                .split('.');
            const options = {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            };
            parts[0] = new Intl.NumberFormat('fa-IR', options).format(parseInt(parts[0], 0));
            formattedNumber = parts.join('.');
        }
    }
    if (market && includeCommas && formatAsUSD) {
        formattedNumber = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(number);
    }

    return formattedNumber;
}

// export const generateAvatarFallback = (str: string) => {
//     const regex = /^(\w).*?\s(\w)|^(\w).*?$/;
//     const res = regex.exec(str);
//
//     if (res) {
//         // If there's a match with space, use the first letter of the first and second words
//         if (res[1] && res[2]) {
//             return res[1].toUpperCase() + res[2].toUpperCase();
//         }
//         // If there's only one word, use the first letter twice
//         if (res[3]) {
//             return res[3].toUpperCase() + res[3].toUpperCase();
//         }
//     }
//
//     // Default fallback if the input doesn't match the expected pattern
//     return '';
// };

const percentFa = new Intl.NumberFormat('fa-IR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * Words for a percent change in a sentence: «۱٫۴۶٪ افت», «۲٫۱۰٪ رشد», «بدون تغییر».
 * `text` is the unsigned Persian percentage (the word gives the direction); it is empty at zero.
 * Used for the 24h sentence on symbol pages, so it must read the same on the server and in the browser.
 */
export function getPriceChangeDetails(change: number) {
    const value = Number(change);
    if (Number.isFinite(value) && value > 0) {
        return { text: `${percentFa.format(value)}٪`, onlyText: 'رشد', color: '#0CAB00' };
    }
    if (Number.isFinite(value) && value < 0) {
        return { text: `${percentFa.format(Math.abs(value))}٪`, onlyText: 'افت', color: '#EB0000' };
    }
    return { text: '', onlyText: 'بدون تغییر', color: '#6B7280' };
}
